import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import styles from './hash-generator.component.module.scss';
import { FullscreenHost } from '../fullscreen-host.component';
import { genStyles } from '../converter-layout/generator-layout';

interface HashState {
    mode: 'text' | 'file';
    input: string;
    fileName: string;
    hashes: { algo: string; value: string }[];
    computing: boolean;
    compareField: string | null;
    compareValue: string;
    copiedField: string | null;
    error: string;
}

async function sha(algo: string, data: ArrayBuffer): Promise<string> {
    const hash = await crypto.subtle.digest(algo, data);
    return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
}

async function md5(data: ArrayBuffer): Promise<string> {
    const SparkMD5 = (await import('spark-md5')).default;
    const spark = new SparkMD5.ArrayBuffer();
    spark.append(data);
    return spark.end();
}

async function computeHashes(data: ArrayBuffer): Promise<{ algo: string; value: string }[]> {
    const [md5Hash, sha1, sha256, sha512] = await Promise.all([
        md5(data), sha('SHA-1', data), sha('SHA-256', data), sha('SHA-512', data),
    ]);
    return [
        { algo: 'MD5', value: md5Hash },
        { algo: 'SHA-1', value: sha1 },
        { algo: 'SHA-256', value: sha256 },
        { algo: 'SHA-512', value: sha512 },
    ];
}

export default class HashGeneratorComponent extends Component<{}, HashState> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;

    state: HashState = {
        mode: 'text', input: '', fileName: '', hashes: [], computing: false,
        compareField: null, compareValue: '', copiedField: null, error: '',
    };

    hashText = async (text: string) => {
        this.setState({ input: text, error: '' });
        const data = new TextEncoder().encode(text).buffer;
        const hashes = await computeHashes(data as ArrayBuffer);
        this.setState({ hashes });
    };

    handleTextInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.hashText(e.target.value);
    };

    handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 2 * 1024 * 1024 * 1024) {
            this.setState({ error: 'File exceeds 2GB limit for browser hashing' });
            return;
        }
        this.setState({ computing: true, fileName: file.name, error: '' });
        try {
            const data = await file.arrayBuffer();
            const hashes = await computeHashes(data);
            this.setState({ hashes, computing: false });
        } catch {
            this.setState({ error: 'Could not read file. It may be corrupted or access was denied.', computing: false });
        }
    };

    copyToClipboard = (value: string, field: string) => {
        navigator.clipboard.writeText(value).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copiedField: field });
            this.copyTimeout = setTimeout(() => this.setState({ copiedField: null }), 2000);
        });
    };

    clear = () => {
        this.setState({ input: '', fileName: '', hashes: [], error: '', compareField: null, compareValue: '', copiedField: null });
    };

    render() {
        const { mode, input, fileName, hashes, computing, compareField, compareValue, copiedField, error } = this.state;

        return (
            <FullscreenHost>
            <div className="row">
                <div className="col-12">
                    <div className={genStyles.section}>
                        <div className={styles.tabs}>
                            <button className={`${styles.tab} ${mode === 'text' ? styles.tabActive : ''}`}
                                onClick={() => this.setState({ mode: 'text' })}>Text</button>
                            <button className={`${styles.tab} ${mode === 'file' ? styles.tabActive : ''}`}
                                onClick={() => this.setState({ mode: 'file' })}>File</button>
                            <div style={{ flex: 1 }} />
                            <button className={sharedStyles.copyButton} onClick={this.clear}>✕ Clear</button>
                        </div>

                        {mode === 'text' ? (
                            <textarea className={sharedStyles.textarea} placeholder="Enter text to hash..."
                                value={input} onChange={this.handleTextInput} rows={4} />
                        ) : (
                            <div className={styles.dropZone}>
                                <input type="file" onChange={this.handleFile} className={styles.fileInput} id="hash-file" />
                                <label htmlFor="hash-file" className={styles.dropLabel}>
                                    {fileName ? `📄 ${fileName}` : 'Drop a file here or click to upload'}
                                </label>
                            </div>
                        )}
                        {error && <div className={sharedStyles.errorMessage} aria-live="polite">⚠ {error}</div>}
                        {computing && <div className={styles.computing}>Computing hashes...</div>}
                    </div>
                </div>

                {hashes.length > 0 && (
                    <div className="col-12">
                        <div className={`${genStyles.section} ${styles.results}`}>
                            {hashes.map(h => {
                                const isCopied = copiedField === h.algo;
                                const isComparing = compareField === h.algo;
                                const match = isComparing && compareValue.trim()
                                    ? compareValue.trim().toLowerCase() === h.value.toLowerCase()
                                    : null;

                                return (
                                    <div key={h.algo} className={styles.hashRow}>
                                        <div className={styles.hashHeader}>
                                            <span className={styles.algoLabel}>{h.algo}</span>
                                            <div className={styles.hashActions}>
                                                <button className={`${sharedStyles.copyButton} ${isCopied ? sharedStyles.copyButtonSuccess : ''}`}
                                                    onClick={() => this.copyToClipboard(h.value, h.algo)}>
                                                    {isCopied ? '✓ Copied' : 'Copy'}
                                                </button>
                                                <button className={`${sharedStyles.copyButton} ${isComparing ? styles.compareActive : ''}`}
                                                    onClick={() => this.setState({ compareField: isComparing ? null : h.algo, compareValue: '' })}>
                                                    Compare
                                                </button>
                                            </div>
                                        </div>
                                        <code className={styles.hashValue}>{h.value}</code>
                                        {isComparing && (
                                            <div className={styles.compareRow}>
                                                <input type="text" className={`${sharedStyles.textarea} ${styles.compareInput}`}
                                                    placeholder="Paste expected hash here..."
                                                    value={compareValue}
                                                    onChange={e => this.setState({ compareValue: e.target.value })} />
                                                {match !== null && (
                                                    <span className={match ? sharedStyles.successIndicator : styles.mismatch}>
                                                        {match ? '✓ Match' : '✗ No match'}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
            </FullscreenHost>
        );
    }
}
